import { Constants } from '../../../../_common/platform/ts/Constants';
import * as MenusData from '../../tsx/menus/Common';
import { AppDispatcher as Dispatcher } from '../dispatcher/AppDispatcher';

import { NotificationType } from '../stores/NotificationsStore';
import * as appTheme from '../../../../_common/platform/ts/appTheme';

import { IActiveWidgets, IErrorMessage, IURLQuery, IWidget, IWidgetState } from '../Interfaces';

import { Action as WidgetAction } from '../../../../_common/platform/ts/WidgetBaseModule';
import * as DrillDownModule from '../../../../_common/platform/tsx/filtering/DrillDownModule';
import { IGlobalAttributeFilterCollection } from '../../../../_common/platform/tsx/filtering/Interfaces';
import { IBackendMethodConfiguratorValue } from '../../../../_common/platform/tsx/BackendMethodConfigurator';
import { ICreateLinkOptions, ICreateWidgetLinkOptions } from '../stores/WorkspaceStore';
import { IDataEditValues } from '../../../../_common/platform/tsx/dataEdit/Interfaces';
import { IExportWidgetToExcelOptions } from '../../../../_common/platform/ts/exportToExcel';
import { IWidgetLayout } from '../../tsx/layout/Interfaces';

export interface Action<TActionType = Constants> {
    actionType: TActionType;
}

export interface IPayload<TAction = Action> {
    action: TAction;
}

export function check<TAction extends Action>(action: Action, id: string | number): action is TAction {
    return action.actionType === id;
}

export module I {
    export interface STORE_CACHE_READ extends Action {}

    export interface STORE_CACHE_CLEAN extends Action {
        storeID: number;
    }

    export interface UPDATE_TIME extends Action {}

    export interface SET_AUTH_FIELD extends Action {
        name: string;
        value: any;
    }

    export interface SIGN_IN extends Action {
        name: string;
        password: string;
        rememberMe: boolean;
    }

    export interface SIGN_IN_WITH_OPENID extends Action {
        providerName: string;
        queryString?: string;
    }

    export interface AUTH_FINISH extends Action {
        displayName: string;
        userid: number;
        userToken: string;
        picture?: string;
    }

    export interface AUTH_REFRESH_TOKEN extends Action {
        userToken: string;
    }

    export interface ADD_TAB extends Action {}

    export interface REORDER_TABS extends Action {
        start: number;
        stop: number;
    }

    export interface CLOSE_ALL_OPENED_TABS extends Action {
        exceptTabIndex?: number;
        closeToTheRight?: boolean;
    }

    export interface INotificationOptions {
        reactContent?: any;
        details?: any; // for remainders, {id: messageId}
        manualDestroy?: boolean;
        progress?: boolean;
        deferred?: JQueryDeferred<any>;
        showInPopup?: boolean;
        showTime?: number;
        remainder?: boolean;
    }

    export interface NOTIFICATION extends Action {
        type: NotificationType;
        message: string;
        runTimeWidgetID: number;
        options: INotificationOptions;
    }

    export interface CLEAN_NOTIFICATION extends Action {
        id: number;
    }

    export interface CLEAN_WIDGET_NOTIFICATIONS extends Action {
        runTimeWidgetID: number;
        immediately: boolean;
    }

    export interface CLEAN_REMAINDER_MESSAGE extends Action {
        messageID: number;
    }

    export interface SHOW_LOADING_PANEL extends Action {
        runTimeWidgetID: number;
    }

    export interface HIDE_LOADING_PANEL extends Action {
        runTimeWidgetID: number;
    }

    export interface SAVE_USER_SETTINGS extends Action {
        value: any;
    }

    export interface RECOVER_PASSWORD_REQUEST extends Action {
        email: string;
    }

    export interface RECOVER_PASSWORD_REQUEST_FINISH extends Action {
        email: string;
    }

    export interface RECOVER_PASSWORD_REQUEST_RESET extends Action {}

    export interface CHANGE_PASSWORD extends Action {
        userID: number;
        oldPassword: string;
        newPassword: string;
        confirmPassword: string;
    }

    export interface CHANGE_PASSWORD_FINISH extends Action {}

    export interface UNAUTHORIZED extends Action {}

    export interface PASSWORD_RESET_START extends Action {
        email: string;
        token: string;
        isNew: boolean;
    }

    export interface APPROVE_NEW_DEVICE_START extends Action {
        token: string;
    }

    export interface PASSWORD_RESET_END extends Action {}

    export interface PASSWORD_RESET_REQUEST extends Action {
        email: string;
        token: string;
        password: string;
        passwordRepeat: string;
    }

    export interface PASSWORD_RESET_REQUEST_FINISH extends Action {
        email: string;
    }

    export interface APPROVE_NEW_DEVICE extends Action {
        token: string;
    }

    export interface PASSWORD_GET_REQUIREMENTS_FINISH extends Action {}

    export interface PASSWORD_GET_REQUIREMENTS extends Action {}
    export interface PASSWORD_GET_REQUIREMENTS_FINISH extends Action {
        minLength: number;
        mustHaveMixedLetterCase: boolean;
        mustContainLetterAndDigits: boolean;
    }

    // export interface USER_SETTINGS_SHOW extends Action { }

    // export interface USER_SETTINGS_HIDE extends Action { }

    export interface USER_SETTINGS_LOAD_DESCRIPTION extends Action {}

    export interface USER_SETTINGS_LOAD_DESCRIPTION_FINISH extends Action {}

    export interface MAIN_VIEW_SHOW extends Action {}

    export interface MAIN_VIEW_HIDE extends Action {}

    export interface WORKSPACE_DIRS_HIDE extends Action {}

    export interface INITIAL_LOAD extends Action {
        userToken: string;
    }

    export interface INITIAL_LOAD_FINISH extends Action {
        authorized: boolean;
    }

    export interface SIGN_OUT extends Action {}

    export interface SHOW_ERROR_FORM extends Action {
        error: IErrorMessage;
    }

    export interface DIRS_CONTENT_LOAD extends Action {
        instanceId: string;
        rootId: number;
        rootName: string;
        force: boolean;
        silent: boolean;
    }

    export interface DIRS_USER_GROUP_CONTENT_LOAD extends Action {
        instanceId: string;
        rootId: number;
        rootName: string;
        force: boolean;
        silent: boolean;
    }

    export interface DIRS_CONTENT_LOAD_FINISH extends Action {
        instanceID: string;
    }

    export interface DIRS_USER_GROUP_CONTENT_LOAD_FINISH extends Action {
        instanceID: string;
    }
    export interface DIRS_RENAME_START extends Action {
        id: number;
        instanceID: string;
    }

    export interface DIRS_RENAME_END extends Action {
        id: number;
        instanceID: string;
        value: string;
    }

    export interface DIRS_CREATE_START extends Action {
        parentID: number;
        instanceID: string;
    }

    export interface DIRS_RENAME_FINISH extends Action {
        id: number;
        name: string;
        instanceID: string;
        isNew: boolean;
    }

    export interface DIRS_DELETE extends Action {
        key: number;
        instanceId: string;
        deleteSubItemsToo: boolean;
    }

    export interface DIRS_DELETE_FINISH extends Action {
        success: boolean;
        instanceID: string;
        id: number;
    }

    export interface DIRS_MOVE extends Action {
        key: number;
        parentId: number;
        instanceId: string;
        rootDirId: number;
    }

    export interface DIRS_MOVE_FINISH extends Action {
        success: boolean;
        key: number;
        parentId: number;
        instanceId: string;
        rootDirId: number;
    }

    export interface DIRS_CLEAN_CACHE extends Action {}

    export interface WORKSPACE_SHOW extends Action {
        id: number;
        name: string;
        data: any;
    }

    export interface WORKSPACE_HIDE extends Action {
        id: number;
    }

    export interface WORKSPACE_WIDGET_SET_HIDDEN extends Action {
        id: number;
        hidden: boolean;
        tab: number;
    }

    export interface WORKSPACE_SAVE_STATE extends Action {}

    export interface WORKSPACE_CONTENT_LOAD extends Action {
        id: number;
    }

    export interface WORKSPACE_CONTENT_LOAD_FINISH extends Action {
        id: number;
    }

    export interface WORKSPACE_SAVE extends Action {
        id: number;
        data: any;
        dateLastUpdated: string;
        def: JQueryDeferred<any>;
    }

    export interface WORKSPACE_SAVE_AS extends Action {
        id: number;
        data: any;
        close: boolean;
        def: JQueryDeferred<any>;
    }

    export interface WORKSPACE_SAVE_FINISH extends Action {
        success: boolean;
        id: number;
        dirId: number;
        name: string;
        data: string;
        dateUpdated: string;
        favouritesOrigin: boolean;
        origin: number;
        state: any;
    }
    export interface WORKSPACE_NOTIFY_FOREIGN_UPDATE extends Action {
        id: number;
        dateUpdated: string;
        updatedBy: number;
    }

    export interface WORKSPACE_RESET extends Action {
        id: number;
    }

    export interface WORKSPACE_RESET_FINISH extends Action {
        id: number;
    }

    export interface MULTI_WIDGET_WORKSPACE_OFFSET extends Action {
        workspace: number;
        widgetID: number;
        value: any;
    }

    export interface WORKSPACE_CREATE_NEW extends Action {
        cid: number;
    }

    export interface WORKSPACE_RENAME extends Action {
        id: number;
        value: string;
    }

    export interface WORKSPACE_DIRS_RENAME extends Action {
        id: number;
        newName: string;
        instanceID: string;
        favouritesOrigin: boolean;
    }

    export interface WORKSPACE_RENAME_FINISH extends Action {
        id: number;
        instanceId: string;
        favouritesOrigin: boolean;
    }

    export interface WORKSPACE_MOVE extends Action {
        id: number;
        moveFromFolderID: number;
        moveToFolderID: number;
        instanceID: string;
        overwrite: boolean;
    }

    export interface WORKSPACE_MOVE_FINISH extends Action {
        id: number;
        moveFromFolderID: number;
        moveToFolderID: number;
        instanceID: string;
    }

    export interface WORKSPACE_CUSTOM_MENU_LOAD extends Action {
        reload: boolean;
    }

    export interface WORKSPACE_CUSTOM_MENU_LOAD_FINISH extends Action {}

    export interface WORKSPACE_SET_ACTIONS_MENU_IS_SHOWN extends Action {
        value: boolean;
    }

    export interface WORKSPACE_SHOW_DIR_SELECT extends Action {}

    export interface WORKSPACE_HIDE_DIR_SELECT extends Action {}

    export interface WORKSPACE_CREATE_SAVE extends Action {
        dirId: number;
        name: string;
        overwrite: boolean; //VR
        data: string;
        origin: number;
        close: boolean;
        state: any;
    }

    export interface WORKSPACE_FAVOURITES_ADD extends Action {
        id: number;
        name: string;
        instanceID: string;
    }

    export interface WORKSPACE_FAVOURITES_ADD_FINISH extends Action {
        id: number;
        name: string;
        instanceID: string;
    }

    export interface WORKSPACE_FAVOURITES_REMOVE extends Action {
        id: number;
        instanceID: string;
    }

    export interface WORKSPACE_FAVOURITES_REMOVE_FINISH extends Action {
        id: number;
    }

    export interface WORKSPACE_SET_STATE extends Action {
        id: number;
        tab: number;
        namespace: string;
        state: any;
    }

    export interface WORKSPACE_BUFFERED_DATA_CHANGED extends Action {
        id: number;
        tab: number;
        runTimeWidgetID: number;
    }

    export interface WORKSPACE_SET_THEME extends Action {
        id: number;
        tab: number;
        value: any;
    }

    export interface WORKSPACE_CLEAR_CACHE extends Action {
        id: number;
    }

    export interface WORKSPACE_SHOW_SIDEBAR extends Action {}

    export interface WORKSPACE_HIDE_SIDEBAR extends Action {}

    export interface WORKSPACE_GET_INFO extends Action {
        ids: number[];
    }

    export interface WORKSPACE_GET_INFO_FINISH extends Action {
        ids: number[];
    }

    export interface WORKSPACE_UPDATE_INITIATE extends Action {
        auto: boolean;
        workspace: number;
        tab: number;
        targetWidgetID: number;
        sourceWidgetID: number;
    }

    export interface WORKSPACE_BEFORE_CLOSE extends Action {
        id: number;
        tabIndex: number;
    }

    export interface WORKSPACE_LAYOUT_RESET extends Action {
        id: number;
    }

    export interface WORKSPACE_LAYOUT_SET extends Action {
        id: number;
        layout: IWidgetLayout;
    }

    export interface WORKSPACE_DELETE extends Action {
        id: number;
        instanceId: string;
        def: JQueryDeferred<any>;
    }

    export interface WORKSPACE_DELETE_FINISH extends Action {
        success: boolean;
        id: number;
        instanceId: string;
    }

    export interface WORKSPACE_VIEW_SHOW extends Action {
        id: number;
        stateId: number;
        distractFree: boolean;
        activeWidgets: IActiveWidgets;
    }

    export interface WORKSPACE_VIEW_HIDE extends Action {
        id: number;
    }

    export interface WORKSPACE_WIDGET_VIEW_OPEN extends Action {
        workspace: number;
        widget: number | string;
        stateId: number;
        data: any;
        query?: { [key: string]: string };
    }

    export interface WORKSPACE_APP_VIEW_OPEN extends Action {
        workspace: number;
    }

    export interface WORKSPACE_APP_VIEW_CLOSE extends Action {
        workspace: number;
    }

    export interface WORKSPACE_VIEW_CLOSE extends Action {
        id: number;
        tabIndex: number;
        clearWorkspaceCache: boolean;
        doNotCloseTab: boolean;
    }

    export interface WORKSPACE_VIEW_CONTENT_LOAD extends Action {
        id: number;
        silent: boolean;
        stateId: number;
        distractFree: boolean;
    }

    export interface WORKSPACE_VIEW_CONTENT_LOAD_FINISH extends Action {
        id: number;
        success: boolean;
    }

    export interface WORKSPACE_ADD_WIDGET extends Action {
        id: number;
        theme: appTheme.IValuesCollection;
        tabID: number;
        pos: number | any[];
    }

    export interface WORKSPACE_TEMPLATE_LOAD extends Action {
        id: number;
    }

    export interface WORKSPACE_TEMPLATE_LOAD_FINISH extends Action {}

    export interface WORKSPACE_TEMPLATES_EXPLORER_SHOW extends Action {}

    export interface WORKSPACE_TEMPLATES_EXPLORER_HIDE extends Action {}

    export interface WORKSPACE_CREATE_LINK extends Action {
        workspace: number;
        tab: number;
        name: string;
        params: string;
        options: ICreateLinkOptions;
    }

    export interface WORKSPACE_DO_DRILL_DOWN extends Action {
        workspace: number;
        drillDownWidgetID: number;
        level: number;
        query: IURLQuery;
    }

    export interface WORKSPACE_DRILL_DOWN_RESET extends Action {
        workspace: number;
    }

    export interface SHOW_WORKSPACE_TEMPLATE_SIDEBAR extends Action {
        workspace: number;
        show: boolean;
    }

    export interface TEMPLATE_SHOW extends Action {
        id: number;
    }

    export interface TEMPLATE_HIDE extends Action {
        id: number;
    }

    export interface TEMPLATE_CONTENT_LOAD extends Action {
        id: number;
    }

    export interface TEMPLATE_CONTENT_LOAD_FINISH extends Action {
        id: number;
    }

    export interface TEMPLATE_SAVE extends Action {
        id: number;
        data: any;
    }

    export interface TEMPLATE_SAVE_FINISH extends Action {
        id: number;
        dirId: number;
        name: string;
        data: any;
    }

    export interface TEMPLATE_CREATE_NEW extends Action {
        dirId: number;
    }

    export interface TEMPLATE_RENAME extends Action {
        id: number;
        value: string;
    }

    export interface TEMPLATE_RENAME_START extends Action {
        id: number;
        instanceId: string;
    }

    export interface TEMPLATE_RENAME_END extends Action {
        id: number;
        value: string;
        instanceId: string;
    }

    export interface TEMPLATE_RENAME_FINISH extends Action {
        id: number;
        instanceId: string;
    }

    export interface TEMPLATE_MOVE extends Action {
        id: number;
        overwrite: boolean;
        currentDirId: number;
        dirId: number;
        instanceId: string;
    }

    export interface TEMPLATE_MOVE_FINISH extends Action {
        success: boolean;
        id: number;
        currentDirId: number;
        dirId: number;
        instanceId: string;
    }

    export interface TEMPLATE_CREATE_SAVE extends Action {
        dirId: number;
        name: string;
        overwrite: boolean;
        data: any;
    }

    export interface TEMPLATE_DELETE extends Action {
        id: number;
        instanceId: string;
    }

    export interface TEMPLATE_DELETE_FINISH extends Action {
        success: boolean;
        id: number;
        instanceId: string;
    }

    export interface TEMPLATE_SET_LOCK extends Action {
        id: number;
        type: string;
        fieldName: string;
        value: boolean;
    }

    export interface TEMPLATES_EXPLORER_SHOW extends Action {}

    export interface TEMPLATES_EXPLORER_HIDE extends Action {}

    export interface TEMPLATES_EXPLORER_CLEAR extends Action {
        instanceId: string;
    }

    export interface TEMPLATES_EXPLORER_SINGLE_LOAD extends Action {
        id: number;
        instanceId: string;
    }

    export interface TEMPLATES_EXPLORER_SINGLE_LOAD_FINISH extends Action {
        id: number;
        instanceId: string;
    }

    export interface TEMPLATES_EXPLORER_SET_DISPLAY_ID extends Action {
        id: number;
        instanceId: string;
    }

    export interface WORKSPACES_EXPLORER_SHOW extends Action {}

    export interface WORKSPACES_EXPLORER_HIDE extends Action {}

    export interface WORKSPACES_EXPLORER_CLEAR extends Action {
        instanceId: string;
    }

    export interface WORKSPACES_EXPLORER_SINGLE_LOAD extends Action {
        id: string | number;
        instanceId: string;
    }

    export interface WORKSPACES_EXPLORER_SINGLE_LOAD_FINISH extends Action {
        id: string | number;
        instanceId: string;
    }

    export interface WORKSPACES_EXPLORER_SET_DISPLAY_ID extends Action {
        id: string | number;
        instanceId: string;
    }

    export interface SET_WORKSPACE_CONTENT_CHANGED extends Action {
        tabId: number;
        widget: number;
        contentChanged: boolean;
    }

    export interface DROP_WORKSPACE_QUERY extends Action {
        tabID: number;
    }

    export interface WIDGET_EDIT extends Action {
        workspace: number;
        data: IWidget;
        theme: appTheme.IValuesCollection;
        tabID: number;
        readOnly?: boolean;
    }

    export interface WIDGET_EDIT_SHOW extends Action {}

    export interface WIDGET_EDIT_HIDE extends Action {}

    export interface WIDGET_SAVE extends Action {
        id: number;
        data: IWidget;
        ignoreWorkspace: boolean;
        local: boolean;
        workspaceID: number;
    }

    export interface WIDGET_CREATE extends Action {
        wsId: number;
        cid: number;
        data: IWidget;
        parentWidgetId?: number;
        wsName?: string;
        isVirtual?: boolean;
    }

    export interface WIDGET_CREATE_FROM_WORKSPACE extends Action {
        cid: number;
        transform: (string) => string;
    }

    export interface WIDGET_REMOVE extends Action {
        id: number;
    }

    export interface WIDGET_DETACH extends Action {
        id: number;
    }

    export interface WIDGET_ATTACH extends Action {
        wsId: number;
        id: number;
        x: number;
        y: number;
        remainSize: boolean;
        fresh: boolean;
        parentWidgetId?: number;
    }

    export interface WIDGET_CREATE_LINK_CALL_DIALOG extends Action {
        workspace: number;
        widget: number;
    }

    export interface WIDGET_CREATE_LINK extends Action {
        workspace: number;
        widget: number;
        name: string;
        state: any;
        widgetData: any;
        options: ICreateWidgetLinkOptions;
    }

    export interface WIDGET_SET_STATE extends Action {
        widget: number;
        widgetState: IWidgetState;
    }

    export interface WIDGET_MODULE_LOAD_START extends Action {
        type: string;
    }

    export interface WIDGET_MODULE_LOAD_FINISH extends Action {
        type: string;
    }

    export interface WIDGET_SET_LAYOUT extends Action {
        workspace: number;
        widget: number;
        layout: IWidgetLayout;
    }

    export interface WIDGET_ADD_FIELD extends Action {
        workspace: number;
        widget: number;
        x: number;
        y: number;
        field: string;
    }

    export interface WIDGET_REMOVE_FIELD extends Action {
        workspace: number;
        widget: number;
        positionIndex: number;
        shift: number;
    }

    export interface WIDGET_SET_HIDDEN extends Action {
        tab: number;
        workspace: number;
        widget: number;
        hidden: boolean;
    }

    export interface WIDGET_SET_HIDDEN extends Action {
        tab: number;
        workspace: number;
        widget: number;
        hidden: boolean;
    }

    export interface WIDGET_DO_CLICK_TO_FILTER extends Action {
        storeID: number;
        dataSourceID: number;
        attributes: DrillDownModule.IDDAttribute[];
        mode: DrillDownModule.ClickToFilterMode;
        useAlways?: boolean;
    }

    export interface WIDGET_DO_DRILL_DOWN extends Action {
        tabID: number;
        drillDownWidgetID: number;
        level: number;
        dataSourceID: number;
        newAttributeFiltersCollection: IGlobalAttributeFilterCollection;
    }

    export interface WIDGET_UPDATE_OPTIONS extends Action {
        workspace: number;
        widget: number;
        options: appTheme.IValuesCollection;
    }

    export interface WIDGET_ADD_CHILD extends Action {
        wsid: number;
        id: number;
        pos: number | any[];
        theme: appTheme.IValuesCollection;
        tabID: number;
    }

    export interface WIDGET_SET_ROLE extends Action {
        tab: number;
        widget: number;
        role: string;
    }

    export interface WIDGET_UNSET_ROLE extends Action {
        tab: number;
        widget: number;
    }

    export interface WIDGET_EXPORT_TO_EXCEL extends Action {
        widget: number;
        options: IExportWidgetToExcelOptions;
    }

    export interface WORKSPACE_WIDGET_EXPORT_TO_EXCEL extends Action {
        widget: number;
        options: any;
    }

    export interface WIDGET_EXPORT_TO_EXCEL_FINISH extends Action {
        widget: number;
    }

    export interface WIDGET_EXPORT_TO_PDF extends Action {
        workspace: number;
        widget: string;
        layout: string;
        papersize: string;
        backendMethodConfig?: IBackendMethodConfiguratorValue;
    }

    export interface WIDGET_MULTIPLE_SET_ACTIVE extends Action {
        workspace: number;
        parentWidgetID: number;
        activeWidgetID: number;
    }

    export interface WIDGET_DRILLDOWN_SET_STATE extends Action {
        workspace: number;
        drillDownWidget: number;
        targetWidget: number;
    }

    export interface WIDGET_RESET_LAYOUT extends Action {
        widget: number;
    }

    export interface WIDGET_REPAINT_ON_DOCK extends Action {}

    export interface WORKSPACES_NAV_SET_SECTION extends Action {
        menuItem: MenusData.IMenuItem;
        mode: MenusData.MenuModes;
    }

    export interface TOOLBAR_BUTTONS_CANCEL_ON extends Action {}

    export interface TOOLBAR_BUTTONS_CANCEL_OFF extends Action {}

    export interface TOOLBAR_BUTTONS_SWITCH_APPLY extends Action {
        tabID: number;
        value: boolean;
    }

    export interface TOGGLE_FULL_SCREEN_MODE extends Action {}

    export interface SET_LOCK_ESCAPE extends Action {
        value: boolean;
    }

    export interface PERMISSIONS_INITIAL_DATA_LOAD extends Action {}

    export interface PERMISSIONS_INITIAL_DATA_LOAD_FINISH extends Action {
        success: boolean;
    }

    export interface PERMISSIONS_LOAD_DIR extends Action {
        dirid: number;
        name: string;
    }

    export interface PERMISSIONS_LOAD_DIR_FINISH extends Action {
        success: boolean;
    }

    export interface PERMISSIONS_DIR_SHOW extends Action {
        dirid: number;
        name: string;
    }
    export interface ADD_HOME_DIR extends Action {
        userid: number;
        name: string;
    }
    export interface DELETE_HOME_DIR extends Action {
        userid: number;
    }

    export interface GET_HOME_DIR extends Action {
        userid: number;
    }

    export interface PERMISSIONS_DIR_HIDE extends Action {}

    export interface COMPONENT_SHOW extends Action {
        moduleName: string;
        componentProps: any;
    }
    export interface PERMISSIONS_DIR_SAVE extends Action {
        dirid: number;
        name: string;
        permissions: any;
    }

    export interface CONFIG_SET_FIELD_VALUES extends Action {
        id: number;
        type: string;
        values: any;
    }

    export interface CONFIG_SET_WIDGET_TYPE extends Action {
        id: number;
        type: string;
    }

    export interface CONFIG_SET_WIDGET_LAYOUT extends Action {
        id: number;
        layout: any;
    }

    export interface THEME_GET extends Action {
        id: number;
    }

    export interface THEME_GET_FINISH extends Action {
        success: boolean;
        id: number;
    }

    export interface THEME_GET_LIST extends Action {
        force: boolean;
    }

    export interface THEME_GET_LIST_FINISH extends Action {
        success: boolean;
    }

    export interface THEME_CREATE extends Action {
        def: JQueryDeferred<any>;
        name: string;
        data: any;
    }

    export interface THEME_CREATE_FINISH extends Action {
        success: boolean;
        name: string;
        data: any;
    }

    export interface THEME_UPDATE extends Action {
        def: JQueryDeferred<any>;
        id: number;
        name: string;
        data: any;
    }

    export interface THEME_UPDATE_FINISH extends Action {
        success: boolean;
        id: number;
        name: string;
        data: any;
    }

    export interface THEME_RENAME extends Action {
        id: number;
        NewName: string;
    }

    export interface THEME_RENAME_FINISH extends Action {
        success: boolean;
        id: number;
        NewName: string;
    }

    export interface THEME_DELETE extends Action {
        id: number;
    }

    export interface THEME_DELETE_FINISH extends Action {
        success: boolean;
        id: number;
    }

    export interface APP_SETTINGS_GET_DESCRIPTION extends Action {}

    export interface APP_SETTINGS_GET_DESCRIPTION_FINISH extends Action {}

    export interface APP_SETTINGS_GET extends Action {
        names?: string[];
        settings?: any;
        forEdit?: boolean;
    }

    export interface APP_SETTINGS_GET_FINISH extends Action {
        data?: any;
    }

    export interface APP_SETTINGS_GET_DEFAULT_THEME extends Action {}

    export interface APP_SETTINGS_GET_DEFAULT_THEME_FINISH extends Action {
        data?: any;
    }

    export interface APP_SETTINGS_SET extends Action {
        values: any;
    }

    export interface APP_SETTINGS_TEST_EMAIL_SEND extends Action {
        values: any;
    }

    export interface APP_SETTINGS_SET_FINISH extends Action {
        data?: any;
    }

    export interface SET_WIDGET_TAB_THEME extends Action {
        tab: number;
        theme: any;
    }

    export interface USER_PERMISSIONS_LOAD_DESCRIPTION extends Action {}

    export interface USER_PERMISSIONS_LOAD_USER extends Action {
        user: number;
    }

    export interface USERS_GROUPS_DATA_UPDATED extends Action {}

    export interface PAGE_VISIBILITY_CHANGE extends Action {
        isVisibile: boolean;
    }

    export interface IAbstractActionPayload<TType = string> {
        type: TType;
        action?: WidgetAction;
        data?: any;
    }

    export interface ABSTRACT_ACTION extends Action {
        workspace: number;
        tab: number;
        widget: number;
        payload: IAbstractActionPayload;
    }

    export interface FILTERS_ARE_ON_SHELF extends Action {
        workspace: number;
        tab: number;
        namespace: string;
    }

    export interface FILTERS_RESET extends Action {
        workspace: number;
        tab: number;
        namespace: string;
        widgetID?: number;
    }

    export type ChangesType = 'filters' | 'data';

    export interface WORKSPACE_APPLY_CHANGES extends Action {
        tab: number;
        namespace: string;
        changesType: ChangesType;
    }

    export interface ICallDataEditDialogOptions {
        attributeID?: any;
        paramsValues?: IDataEditValues;
    }

    export interface CALL_DATA_EDIT_DIALOG extends Action {
        tabID: number;
        options?: ICallDataEditDialogOptions;
        sourceWidgetID?: number;
        customDialogID?: string;
    }

    export interface LOAD_DATA<IParams> extends Action {
        storeID: number;
        parameters: IParams;
    }

    export interface LOAD_DATA_FINISH extends Action {
        storeID: number;
    }
}

export class AppActions {
    constructor(private dispatcher: Dispatcher) {}

    public static get(dispatcher: Dispatcher) {
        return new AppActions(dispatcher);
    }

    public storeCacheRead() {
        this.dispatcher.handleViewAction<I.STORE_CACHE_READ>({
            actionType: Constants.STORE_CACHE_READ
        });
    }

    public storeCacheClean(storeID?: number) {
        this.dispatcher.handleViewAction<I.STORE_CACHE_CLEAN>({
            actionType: Constants.STORE_CACHE_CLEAN,
            storeID: storeID
        });
    }

    public updateTime() {
        this.dispatcher.handleViewAction<I.UPDATE_TIME>({
            actionType: Constants.UPDATE_TIME
        });
    }

    public setAuthField(name: string, value: any) {
        this.dispatcher.handleViewAction<I.SET_AUTH_FIELD>({
            actionType: Constants.SET_AUTH_FIELD,

            name: name,
            value: value
        });
    }

    public signIn(name: string, password: string, rememberMe: boolean) {
        this.dispatcher.handleViewAction<I.SIGN_IN>({
            actionType: Constants.SIGN_IN,

            name: name,
            password: password,
            rememberMe: rememberMe
        });
    }

    public signInWithOpenID(providerName: string, queryString?: string) {
        this.dispatcher.handleViewAction<I.SIGN_IN_WITH_OPENID>({
            actionType: Constants.SIGN_IN_WITH_OPENID,

            providerName: providerName,
            queryString: queryString
        });
    }

    public authFinish(displayName: string, userid: number, userToken: string, picture?: string) {
        this.dispatcher.handleViewAction<I.AUTH_FINISH>({
            actionType: Constants.AUTH_FINISH,

            userid: userid,
            displayName: displayName,
            userToken: userToken,
            picture: picture
        });
    }

    public authRefreshToken(userToken: string) {
        this.dispatcher.handleViewAction<I.AUTH_REFRESH_TOKEN>({
            actionType: Constants.AUTH_REFRESH_TOKEN,

            userToken: userToken
        });
    }

    public addTab() {
        this.dispatcher.handleViewAction<I.ADD_TAB>({
            actionType: Constants.ADD_TAB
        });
    }

    public reorderTabs(start: number, stop: number) {
        this.dispatcher.handleViewAction<I.REORDER_TABS>({
            actionType: Constants.REORDER_TABS,

            start: start,
            stop: stop
        });
    }

    public closeAllOpenedTabs(exceptTabIndex?: number, closeToTheRight?: boolean) {
        this.dispatcher.handleViewAction<I.CLOSE_ALL_OPENED_TABS>({
            actionType: Constants.CLOSE_ALL_OPENED_TABS,

            exceptTabIndex: exceptTabIndex,
            closeToTheRight: closeToTheRight
        });
    }

    public showNotification_Success(message: string, runTimeWidgetID?: number, options?: I.INotificationOptions) {
        this.showNotification('success', message, runTimeWidgetID, options);
    }

    //EVG:
    // It is not correct to use 'warning' type for showing NO DATA AVAILABLE message, but 'success' is not one might feel right to use.
    // That's why I've added "Info" for to hint that 'warning' is not a universal type.
    public showNotification_Info(message: string, runTimeWidgetID?: number, options?: I.INotificationOptions) {
        this.showNotification('success', message, runTimeWidgetID, options);
    }

    public showNotification_Fail(message: string, runTimeWidgetID?: number, options?: I.INotificationOptions) {
        this.showNotification('fail', message, runTimeWidgetID, options);
    }

    public showNotification_Warning(message: string, runTimeWidgetID?: number, options?: I.INotificationOptions) {
        this.showNotification('warning', message, runTimeWidgetID, options);
    }

    public showNotification(
        type: NotificationType,
        message: string,
        runTimeWidgetID?: number,
        options?: I.INotificationOptions
    ) {
        this.dispatcher.handleViewAction<I.NOTIFICATION>({
            actionType: Constants.NOTIFICATION_SHOW,

            type: type,
            message: message,
            runTimeWidgetID: runTimeWidgetID,
            options: options
        });
    }

    //Obsolete.
    //'warning' | 'success' | 'fail'
    public notification(type: NotificationType, message: string, options?: I.INotificationOptions) {
        this.dispatcher.handleViewAction<I.NOTIFICATION>({
            actionType: Constants.NOTIFICATION_SHOW,

            type: type,
            message: message,
            runTimeWidgetID: null,
            options: options
        });
    }

    public showLoadingPanel(runTimeWidgetID: number) {
        this.dispatcher.handleViewAction<I.SHOW_LOADING_PANEL>({
            actionType: Constants.LOADING_PANEL_SHOW,

            runTimeWidgetID: runTimeWidgetID
        });
    }

    public hideLoadingPanel(runTimeWidgetID: number) {
        this.dispatcher.handleViewAction<I.HIDE_LOADING_PANEL>({
            actionType: Constants.LOADING_PANEL_HIDE,

            runTimeWidgetID: runTimeWidgetID
        });
    }

    public notificationClean(id: number) {
        this.dispatcher.handleViewAction<I.CLEAN_NOTIFICATION>({
            actionType: Constants.NOTIFICATION_CLEAN,

            id
        });
    }

    public cleanWidgetNotifications(runTimeWidgetID: number, immediately?: boolean) {
        this.dispatcher.handleViewAction<I.CLEAN_WIDGET_NOTIFICATIONS>({
            actionType: Constants.CLEAN_WIDGET_NOTIFICATIONS,

            runTimeWidgetID: runTimeWidgetID,
            immediately: immediately
        });
    }

    public cleanRemainderMessage(messageID: number) {
        this.dispatcher.handleViewAction<I.CLEAN_REMAINDER_MESSAGE>({
            actionType: Constants.CLEAN_REMAINDER_MESSAGE,

            messageID: messageID
        });
    }

    public saveUserSettings(value: any) {
        this.dispatcher.handleViewAction<I.SAVE_USER_SETTINGS>({
            actionType: Constants.SAVE_USER_SETTINGS,

            value: value
        });
    }

    public recoverPasswordRequest(email: string) {
        this.dispatcher.handleViewAction<I.RECOVER_PASSWORD_REQUEST>({
            actionType: Constants.RECOVER_PASSWORD_REQUEST,

            email: email
        });
    }

    public recoverPasswordRequestFinish(email?: string) {
        this.dispatcher.handleViewAction<I.RECOVER_PASSWORD_REQUEST_FINISH>({
            actionType: Constants.RECOVER_PASSWORD_REQUEST_FINISH,

            email: email
        });
    }

    public recoverPasswordRequestReset() {
        this.dispatcher.handleViewAction<I.RECOVER_PASSWORD_REQUEST_RESET>({
            actionType: Constants.RECOVER_PASSWORD_REQUEST_RESET
        });
    }

    public changePassword(userID: number, oldPassword: string, newPassword: string, confirmPassword: string) {
        this.dispatcher.handleViewAction<I.CHANGE_PASSWORD>({
            actionType: Constants.CHANGE_PASSWORD,

            userID: userID,
            oldPassword: oldPassword,
            newPassword: newPassword,
            confirmPassword: confirmPassword
        });
    }

    public changePasswordFinish() {
        this.dispatcher.handleViewAction<I.CHANGE_PASSWORD_FINISH>({
            actionType: Constants.CHANGE_PASSWORD_FINISH
        });
    }

    public unauthorized() {
        this.dispatcher.handleViewAction<I.UNAUTHORIZED>({
            actionType: Constants.UNAUTHORIZED
        });
    }

    public passwordResetStart(email: string, token: string, isNew: boolean) {
        this.dispatcher.handleViewAction<I.PASSWORD_RESET_START>({
            actionType: Constants.PASSWORD_RESET_START,

            email: email,
            token: token,
            isNew: isNew
        });
    }

    public approveNewDeviceStart(token: string) {
        this.dispatcher.handleViewAction<I.APPROVE_NEW_DEVICE_START>({
            actionType: Constants.APPROVE_NEW_DEVICE_START,
            token: token
        });
    }

    public passwordResetEnd() {
        this.dispatcher.handleViewAction<I.PASSWORD_RESET_END>({
            actionType: Constants.PASSWORD_RESET_END
        });
    }

    public passwordResetRequest(email: string, token: string, password: string, passwordRepeat: string) {
        this.dispatcher.handleViewAction<I.PASSWORD_RESET_REQUEST>({
            actionType: Constants.PASSWORD_RESET_REQUEST,

            email: email,
            token: token,
            password: password,
            passwordRepeat: passwordRepeat
        });
    }

    public passwordResetRequestFinish(email?: string) {
        this.dispatcher.handleViewAction<I.PASSWORD_RESET_REQUEST_FINISH>({
            actionType: Constants.PASSWORD_RESET_REQUEST_FINISH,

            email: email
        });
    }

    public passwordGetRequirements() {
        this.dispatcher.handleViewAction<I.PASSWORD_GET_REQUIREMENTS>({
            actionType: Constants.PASSWORD_GET_REQUIREMENTS
        });
    }

    public approveNewDevice(token: string) {
        this.dispatcher.handleViewAction<I.APPROVE_NEW_DEVICE>({
            actionType: Constants.APPROVE_NEW_DEVICE,
            token
        });
    }

    public passwordGetRequirementsFinish(
        minLength: number,
        mustHaveMixedLetterCase: boolean,
        mustContainLetterAndDigits: boolean
    ) {
        this.dispatcher.handleViewAction<I.PASSWORD_GET_REQUIREMENTS_FINISH>({
            actionType: Constants.PASSWORD_GET_REQUIREMENTS_FINISH,

            minLength,
            mustHaveMixedLetterCase,
            mustContainLetterAndDigits
        });
    }

    // public userSettingsShow() {
    //     this.dispatcher.handleViewAction<I.USER_SETTINGS_SHOW>({
    //         actionType: Constants.USER_SETTINGS_SHOW
    //     });
    // }

    // public userSettingsHide() {
    //     this.dispatcher.handleViewAction<I.USER_SETTINGS_HIDE>({
    //         actionType: Constants.USER_SETTINGS_HIDE
    //     });
    // }

    public userSettingsLoadDescription() {
        this.dispatcher.handleViewAction<I.USER_SETTINGS_LOAD_DESCRIPTION>({
            actionType: Constants.USER_SETTINGS_LOAD_DESCRIPTION
        });
    }

    public userSettingsLoadDescriptionFinish() {
        this.dispatcher.handleViewAction<I.USER_SETTINGS_LOAD_DESCRIPTION_FINISH>({
            actionType: Constants.USER_SETTINGS_LOAD_DESCRIPTION_FINISH
        });
    }

    public mainViewShow() {
        this.dispatcher.handleViewAction<I.MAIN_VIEW_SHOW>({
            actionType: Constants.MAIN_VIEW_SHOW
        });
    }

    public mainViewHide() {
        this.dispatcher.handleViewAction<I.WORKSPACE_DIRS_HIDE>({
            actionType: Constants.WORKSPACE_DIRS_HIDE
        });
    }

    public initialLoad(userToken?: string) {
        this.dispatcher.handleViewAction<I.INITIAL_LOAD>({
            actionType: Constants.INITIAL_LOAD,
            userToken
        });
    }

    public initialFinish(authorized: boolean) {
        this.dispatcher.handleViewAction<I.INITIAL_LOAD_FINISH>({
            actionType: Constants.INITIAL_LOAD_FINISH,

            authorized: authorized
        });
    }

    public signOut() {
        this.dispatcher.handleViewAction<I.SIGN_OUT>({
            actionType: Constants.SIGN_OUT
        });
    }

    public showErrorForm(error: IErrorMessage) {
        this.dispatcher.handleViewAction<I.SHOW_ERROR_FORM>({
            actionType: Constants.SHOW_ERROR_FORM,
            error: error
        });
    }

    public dirsContentLoad(instanceId?: string, rootId?: number, rootName?: string, force?: boolean, silent?: boolean) {
        this.dispatcher.handleViewAction<I.DIRS_CONTENT_LOAD>({
            actionType: Constants.DIRS_CONTENT_LOAD,

            rootId: rootId,
            rootName: rootName,
            instanceId: instanceId,
            force: force,
            silent: silent
        });
    }

    public dirsUserGroupContentLoad(
        instanceId?: string,
        rootId?: number,
        rootName?: string,
        force?: boolean,
        silent?: boolean
    ) {
        this.dispatcher.handleViewAction<I.DIRS_USER_GROUP_CONTENT_LOAD>({
            actionType: Constants.DIRS_USER_GROUP_CONTENT_LOAD,

            rootId: rootId,
            rootName: rootName,
            instanceId: instanceId,
            force: force,
            silent: silent
        });
    }

    public dirsContentLoadFinish(instanceId: string) {
        this.dispatcher.handleViewAction<I.DIRS_CONTENT_LOAD_FINISH>({
            actionType: Constants.DIRS_CONTENT_LOAD_FINISH,

            instanceID: instanceId
        });
    }

    public dirsUserGroupContentLoadFinish(instanceId: string) {
        this.dispatcher.handleViewAction<I.DIRS_USER_GROUP_CONTENT_LOAD_FINISH>({
            actionType: Constants.DIRS_USER_GROUP_CONTENT_LOAD_FINISH,

            instanceID: instanceId
        });
    }

    public dirsRenameStart(id: number, instanceID: string) {
        this.dispatcher.handleViewAction<I.DIRS_RENAME_START>({
            actionType: Constants.DIRS_RENAME_START,

            id: id,
            instanceID: instanceID
        });
    }

    public dirsRenameEnd(id: number, value: string, instanceId: string) {
        this.dispatcher.handleViewAction<I.DIRS_RENAME_END>({
            actionType: Constants.DIRS_RENAME_END,

            id: id,
            value: value,
            instanceID: instanceId
        });
    }

    public dirsCreateStart(parentID: number, instanceID: string) {
        this.dispatcher.handleViewAction<I.DIRS_CREATE_START>({
            actionType: Constants.DIRS_CREATE_START,

            parentID: parentID,
            instanceID: instanceID
        });
    }

    public dirsRenameFinish(id: number, name: string, instanceID: string, isNew: boolean) {
        this.dispatcher.handleViewAction<I.DIRS_RENAME_FINISH>({
            actionType: Constants.DIRS_RENAME_FINISH,

            id: id,
            name: name,
            instanceID: instanceID,
            isNew: isNew
        });
    }

    public dirsDelete(
        key: number,
        instanceId: string,
        name: string,
        deleteSubItemsToo: boolean,
        onDelete?: (key: number) => void
    ) {
        this.dispatcher.handleViewAction<I.DIRS_DELETE>({
            actionType: Constants.DIRS_DELETE,

            key: key,
            instanceId: instanceId,
            deleteSubItemsToo: deleteSubItemsToo
        });

        if (typeof onDelete === 'function') {
            onDelete(key);
        }

        // this.createDialog(
        //     `Delete folder '${ name }'`,
        //     `folder '${ name }' will be deleted. Please, confirm this action.`,
        //
        //     () => {},
        //     () => {
        //         this.dispatcher.handleViewAction<I.DIRS_DELETE>(
        //             {
        //                 actionType: Constants.DIRS_DELETE,
        //
        //                 key: key,
        //                 instanceId: instanceId
        //             }
        //         );
        //
        //         if (typeof onDelete === 'function') {
        //             onDelete(key);
        //         }
        //     },
        //
        //     () => {},
        //
        //     'confirm',
        //
        //     {
        //         confirmTitle: null,
        //         cancelTitle: 'Delete'
        //     }
        // );
    }

    public dirsDeleteFinish(success: boolean, key: number, instanceId: string) {
        this.dispatcher.handleViewAction<I.DIRS_DELETE_FINISH>({
            actionType: Constants.DIRS_DELETE_FINISH,

            success: success,
            id: key,
            instanceID: instanceId
        });
    }

    public dirsMove(key: number, parentId: number, instanceId: string, rootDirId: number) {
        this.dispatcher.handleViewAction<I.DIRS_MOVE>({
            actionType: Constants.DIRS_MOVE,

            key: key,
            parentId: parentId,
            instanceId: instanceId,
            rootDirId: rootDirId
        });
    }

    public dirsMoveFinish(success: boolean, key: number, parentId: number, instanceId: string, rootDirId: number) {
        this.dispatcher.handleViewAction<I.DIRS_MOVE_FINISH>({
            actionType: Constants.DIRS_MOVE_FINISH,

            success: success,
            key: key,
            parentId: parentId,
            instanceId: instanceId,
            rootDirId: rootDirId
        });
    }

    public dirsCleanCache() {
        this.dispatcher.handleViewAction<I.DIRS_CLEAN_CACHE>({
            actionType: Constants.DIRS_CLEAN_CACHE
        });
    }

    public workspaceShow(id: number, name?: string, data?: any) {
        this.dispatcher.handleViewAction<I.WORKSPACE_SHOW>({
            actionType: Constants.WORKSPACE_SHOW,
            id: id,
            name: name,
            data: data
        });
    }

    public workspaceHide(id: number) {
        this.dispatcher.handleViewAction<I.WORKSPACE_HIDE>({
            actionType: Constants.WORKSPACE_HIDE,
            id: id
        });
    }

    workspaceSetHidden(id: number, hidden: boolean, tab?: number) {
        this.dispatcher.handleViewAction<I.WORKSPACE_WIDGET_SET_HIDDEN>({
            actionType: Constants.WORKSPACE_WIDGET_SET_HIDDEN,
            id: id,
            hidden: hidden,
            tab: tab
        });
    }

    workspaceSaveState() {
        this.dispatcher.handleViewAction<I.WORKSPACE_SAVE_STATE>({
            actionType: Constants.WORKSPACE_SAVE_STATE
        });
    }

    public workspaceLoadContent(id: number) {
        this.dispatcher.handleViewAction<I.WORKSPACE_CONTENT_LOAD>({
            actionType: Constants.WORKSPACE_CONTENT_LOAD,

            id: id
        });
    }

    public workspaceLoadContentFinish(id: number) {
        this.dispatcher.handleViewAction<I.WORKSPACE_CONTENT_LOAD_FINISH>({
            actionType: Constants.WORKSPACE_CONTENT_LOAD_FINISH,

            id: id
        });
    }

    public workspaceSave(id: number, data: any, dateLastUpdated: string, def?: JQueryDeferred<any>) {
        this.dispatcher.handleViewAction<I.WORKSPACE_SAVE>({
            actionType: Constants.WORKSPACE_SAVE,

            id: id,
            data: data,
            dateLastUpdated: dateLastUpdated,
            def
        });
    }

    public workspaceSaveAs(id: number, data: any, close: boolean, def?: JQueryDeferred<any>) {
        this.dispatcher.handleViewAction<I.WORKSPACE_SAVE_AS>({
            actionType: Constants.WORKSPACE_SAVE_AS,

            id: id,
            data: data,
            close: close,
            def: def
        });
    }

    public workspaceSaveFinish(
        success: boolean,
        id?: number,
        dirId?: number,
        name?: string,
        data?: string,
        dateUpdated?: string,
        favouritesOrigin: boolean = false,
        origin?: number,
        state?: any
    ) {
        this.dispatcher.handleViewAction<I.WORKSPACE_SAVE_FINISH>({
            actionType: Constants.WORKSPACE_SAVE_FINISH,

            success: success,
            id: id,
            dirId: dirId,
            name: name,
            data: data,
            dateUpdated: dateUpdated,
            favouritesOrigin: favouritesOrigin,
            origin: origin,
            state: state
        });
    }

    public workspaceNotifyForeignUpdate(id: number, dateUpdated: string, updatedBy: number) {
        this.dispatcher.handleViewAction<I.WORKSPACE_NOTIFY_FOREIGN_UPDATE>({
            actionType: Constants.WORKSPACE_NOTIFY_FOREIGN_UPDATE,

            id: id,
            dateUpdated: dateUpdated,
            updatedBy: updatedBy
        });
    }

    public workspaceReset(id: number) {
        this.dispatcher.handleViewAction<I.WORKSPACE_RESET>({
            actionType: Constants.WORKSPACE_RESET,

            id: id
        });
    }

    public workspaceResetFinish(id: number) {
        this.dispatcher.handleViewAction<I.WORKSPACE_RESET_FINISH>({
            actionType: Constants.WORKSPACE_RESET_FINISH,

            id: id
        });
    }

    public setWorkspaceOffset(workspace: number, widgetID: number, value: any) {
        this.dispatcher.handleViewAction<I.MULTI_WIDGET_WORKSPACE_OFFSET>({
            actionType: Constants.MULTI_WIDGET_WORKSPACE_OFFSET,

            workspace: workspace,
            widgetID: widgetID,
            value: value
        });
    }

    public workspaceCreateNew(cid: number = null) {
        this.dispatcher.handleViewAction<I.WORKSPACE_CREATE_NEW>({
            actionType: Constants.WORKSPACE_CREATE_NEW,

            cid: cid
        });
    }

    // VR: JEST shows that this f. is not used anywhere in code
    // public workspaceRename(id: number, value: string) {
    //     this.dispatcher.handleViewAction<I.WORKSPACE_RENAME>(
    //         {
    //             actionType: Constants.WORKSPACE_RENAME,
    //
    //             id: id,
    //             value: value
    //         }
    //     );
    // }

    public workspaceDirsRename(id: number, newName: string, instanceId: string, favouritesOrigin: boolean = false) {
        this.dispatcher.handleViewAction<I.WORKSPACE_DIRS_RENAME>({
            actionType: Constants.WORKSPACE_DIRS_RENAME,

            id: id,
            newName: newName,
            instanceID: instanceId,
            favouritesOrigin: favouritesOrigin
        });
    }

    public workspaceMove(
        id: number,
        moveFromFolderID: number,
        moveToFolderID: number,
        instanceID: string,
        overwrite: boolean
    ) {
        this.dispatcher.handleViewAction<I.WORKSPACE_MOVE>({
            actionType: Constants.WORKSPACE_MOVE,
            id: id,
            moveFromFolderID: moveFromFolderID,
            moveToFolderID: moveToFolderID,
            instanceID: instanceID,
            overwrite: overwrite
        });
    }

    public workspaceMoveFinish(id: number, moveFromFolderID: number, moveToFolderID: number, instanceID: string) {
        this.dispatcher.handleViewAction<I.WORKSPACE_MOVE_FINISH>({
            actionType: Constants.WORKSPACE_MOVE_FINISH,

            id: id,
            moveFromFolderID: moveFromFolderID,
            moveToFolderID: moveToFolderID,
            instanceID: instanceID
        });
    }

    public workspaceCustomMenuLoad(reload) {
        this.dispatcher.handleViewAction<I.WORKSPACE_CUSTOM_MENU_LOAD>({
            actionType: Constants.WORKSPACE_CUSTOM_MENU_LOAD,
            reload: reload
        });
    }

    public workspaceCustomMenuLoadFinish() {
        this.dispatcher.handleViewAction<I.WORKSPACE_CUSTOM_MENU_LOAD_FINISH>({
            actionType: Constants.WORKSPACE_CUSTOM_MENU_LOAD_FINISH
        });
    }

    public workspaceSetActionsMenuIsShown(value: boolean) {
        this.dispatcher.handleViewAction<I.WORKSPACE_SET_ACTIONS_MENU_IS_SHOWN>({
            actionType: Constants.WORKSPACE_SET_ACTIONS_MENU_IS_SHOWN,
            value: value
        });
    }

    public workspaceShowDirSelect() {
        this.dispatcher.handleViewAction<I.WORKSPACE_SHOW_DIR_SELECT>({
            actionType: Constants.WORKSPACE_SHOW_DIR_SELECT
        });
    }

    public workspaceHideDirSelect() {
        this.dispatcher.handleViewAction<I.WORKSPACE_HIDE_DIR_SELECT>({
            actionType: Constants.WORKSPACE_HIDE_DIR_SELECT
        });
    }

    public workspaceCreateSave(
        dirId: number,
        name: string,
        overwrite: boolean,
        data: string,
        origin?: number,
        close?: boolean,
        state?: any
    ) {
        this.dispatcher.handleViewAction<I.WORKSPACE_CREATE_SAVE>({
            actionType: Constants.WORKSPACE_CREATE_SAVE,

            dirId: dirId,
            name: name,
            overwrite: overwrite,
            data: data,
            origin: origin,
            close: close,
            state: state
        });
    }

    public workspaceFavouritesAdd(id: number, name: string, instanceID?: string) {
        this.dispatcher.handleViewAction<I.WORKSPACE_FAVOURITES_ADD>({
            actionType: Constants.WORKSPACE_FAVOURITES_ADD,

            id: id,
            name: name,
            instanceID: instanceID
        });
    }

    public workspaceFavouritesAddFinish(id: number, name: string, instanceID?: string) {
        this.dispatcher.handleViewAction<I.WORKSPACE_FAVOURITES_ADD_FINISH>({
            actionType: Constants.WORKSPACE_FAVOURITES_ADD_FINISH,

            id: id,
            name: name,
            instanceID: instanceID
        });
    }

    public workspaceFavouritesRemove(id: number, instanceID: string) {
        this.dispatcher.handleViewAction<I.WORKSPACE_FAVOURITES_REMOVE>({
            actionType: Constants.WORKSPACE_FAVOURITES_REMOVE,

            id: id,
            instanceID: instanceID
        });
    }

    public workspaceFavouritesRemoveFinish(id: number) {
        this.dispatcher.handleViewAction<I.WORKSPACE_FAVOURITES_REMOVE_FINISH>({
            actionType: Constants.WORKSPACE_FAVOURITES_REMOVE_FINISH,

            id: id
        });
    }

    public workspaceSetState(id: number, tab: number, namespace: string, state: any | ((prevState: any) => any)) {
        this.dispatcher.handleViewAction<I.WORKSPACE_SET_STATE>({
            actionType: Constants.WORKSPACE_SET_STATE,
            id: id,
            tab: tab,
            namespace: namespace,
            state: state
        });
    }

    public workspaceBufferedDataChanged(id: number, tab: number, runTimeWidgetID: number) {
        this.dispatcher.handleViewAction<I.WORKSPACE_BUFFERED_DATA_CHANGED>({
            actionType: Constants.WORKSPACE_BUFFERED_DATA_CHANGED,

            id: id,
            tab: tab,
            runTimeWidgetID: runTimeWidgetID
        });
    }

    // public workspaceTransformState(
    //     id: number,
    //     tab: number,
    //     namespace: string,
    //     transform: (prevState: any) => any,
    //     noEmit?: boolean
    // ) {
    //     this.dispatcher.handleViewAction<I.WORKSPACE_TRANSFORM_STATE>({
    //         actionType: Constants.WORKSPACE_TRANSFORM_STATE,
    //         id,
    //         tab,
    //         namespace,
    //         transform,
    //         noEmit
    //     });
    // }

    public workspaceSetTheme(id: number, tab: number, value: any) {
        this.dispatcher.handleViewAction<I.WORKSPACE_SET_THEME>({
            actionType: Constants.WORKSPACE_SET_THEME,
            id: id,
            tab: tab,
            value: value
        });
    }

    public workspaceClearCache(id: number) {
        this.dispatcher.handleViewAction<I.WORKSPACE_CLEAR_CACHE>({
            actionType: Constants.WORKSPACE_CLEAR_CACHE,
            id: id
        });
    }

    public workspaceShowSidebar() {
        this.dispatcher.handleViewAction<I.WORKSPACE_SHOW_SIDEBAR>({
            actionType: Constants.WORKSPACE_SHOW_SIDEBAR
        });
    }

    public workspaceHideSidebar() {
        this.dispatcher.handleViewAction<I.WORKSPACE_HIDE_SIDEBAR>({
            actionType: Constants.WORKSPACE_HIDE_SIDEBAR
        });
    }

    public workspaceGetInfo(ids: number[]) {
        this.dispatcher.handleViewAction<I.WORKSPACE_GET_INFO>({
            actionType: Constants.WORKSPACE_GET_INFO,
            ids
        });
    }

    public workspaceGetInfoFinish(ids: number[]) {
        this.dispatcher.handleViewAction<I.WORKSPACE_GET_INFO_FINISH>({
            actionType: Constants.WORKSPACE_GET_INFO_FINISH,
            ids
        });
    }

    // targetWidgetID - ID of a widget that should be refreshed. If null, all are refreshed.
    // sourceWidgetID - ID of the widget that rized the event. If set, others but this one are refreshed.
    public workspaceUpdateInitiate(
        auto: boolean,
        workspace: number,
        tab: number,
        targetWidgetID?: number,
        sourceWidgetID?: number
    ) {
        this.dispatcher.handleViewAction<I.WORKSPACE_UPDATE_INITIATE>({
            actionType: Constants.WORKSPACE_UPDATE_INITIATE,
            auto: auto,
            workspace: workspace,
            tab: tab,
            targetWidgetID: targetWidgetID,
            sourceWidgetID: sourceWidgetID
        });
    }

    public workspaceBeforeClose(id: number, tabIndex: number) {
        this.dispatcher.handleViewAction<I.WORKSPACE_BEFORE_CLOSE>({
            actionType: Constants.WORKSPACE_BEFORE_CLOSE,
            id: id,
            tabIndex: tabIndex
        });
    }

    public workspaceLayoutReset(id: number) {
        this.dispatcher.handleViewAction<I.WORKSPACE_LAYOUT_RESET>({
            actionType: Constants.WORKSPACE_LAYOUT_RESET,
            id: id
        });
    }

    public workspaceLayoutSet(id: number, layout: IWidgetLayout) {
        this.dispatcher.handleViewAction<I.WORKSPACE_LAYOUT_SET>({
            actionType: Constants.WORKSPACE_LAYOUT_SET,
            id: id,
            layout: layout
        });
    }

    public workspaceRemove(id: number, instanceId?: string, def: JQueryDeferred<any> = null) {
        this.dispatcher.handleViewAction<I.WORKSPACE_DELETE>({
            actionType: Constants.WORKSPACE_DELETE,
            id: id,
            instanceId: instanceId,
            def: def
        });
    }

    public workspaceRemoveFinish(success: boolean, id: number, instanceId?: string) {
        this.dispatcher.handleViewAction<I.WORKSPACE_DELETE_FINISH>({
            actionType: Constants.WORKSPACE_DELETE_FINISH,
            success: success,
            id: id,
            instanceId: instanceId
        });
    }

    public workspaceDoDrillDown(workspace: number, drillDownWidgetID: number, level: number, query: IURLQuery) {
        this.dispatcher.handleViewAction<I.WORKSPACE_DO_DRILL_DOWN>({
            actionType: Constants.WORKSPACE_DO_DRILL_DOWN,
            workspace: workspace,
            drillDownWidgetID: drillDownWidgetID,
            level: level,
            query: query
        });
    }

    public workspaceDrillDownReset(workspace: number) {
        this.dispatcher.handleViewAction<I.WORKSPACE_DRILL_DOWN_RESET>({
            actionType: Constants.WORKSPACE_DRILL_DOWN_RESET,

            workspace: workspace
        });
    }

    public templateShow(id: number) {
        this.dispatcher.handleViewAction<I.TEMPLATE_SHOW>({
            actionType: Constants.TEMPLATE_SHOW,

            id: id
        });
    }

    public templateHide(id: number) {
        this.dispatcher.handleViewAction<I.TEMPLATE_HIDE>({
            actionType: Constants.TEMPLATE_HIDE,

            id: id
        });
    }

    public templateLoadContent(id: number) {
        this.dispatcher.handleViewAction<I.TEMPLATE_CONTENT_LOAD>({
            actionType: Constants.TEMPLATE_CONTENT_LOAD,

            id: id
        });
    }

    public templateLoadContentFinish(id: number) {
        this.dispatcher.handleViewAction<I.TEMPLATE_CONTENT_LOAD_FINISH>({
            actionType: Constants.TEMPLATE_CONTENT_LOAD_FINISH,

            id: id
        });
    }

    public templateSave(id: number, data: any) {
        this.dispatcher.handleViewAction<I.TEMPLATE_SAVE>({
            actionType: Constants.TEMPLATE_SAVE,

            id: id,
            data: data
        });
    }

    public templateSaveFinish(id?: number, dirId?: number, name?: string, data?: any) {
        this.dispatcher.handleViewAction<I.TEMPLATE_SAVE_FINISH>({
            actionType: Constants.TEMPLATE_SAVE_FINISH,

            id: id,
            dirId: dirId,
            name: name,
            data: data
        });
    }

    public templateCreateNew(dirId: number) {
        this.dispatcher.handleViewAction<I.TEMPLATE_CREATE_NEW>({
            actionType: Constants.TEMPLATE_CREATE_NEW,

            dirId: dirId
        });
    }

    public templateRename(id: number, value: string) {
        this.dispatcher.handleViewAction<I.TEMPLATE_RENAME>({
            actionType: Constants.TEMPLATE_RENAME,

            id: id,
            value: value
        });
    }

    public templateRenameStart(id: number, instanceId: string) {
        this.dispatcher.handleViewAction<I.TEMPLATE_RENAME_START>({
            actionType: Constants.TEMPLATE_RENAME_START,

            id: id,
            instanceId: instanceId
        });
    }

    public templateRenameEnd(id: number, value: string, instanceId: string) {
        this.dispatcher.handleViewAction<I.TEMPLATE_RENAME_END>({
            actionType: Constants.TEMPLATE_RENAME_END,

            id: id,
            value: value,
            instanceId: instanceId
        });
    }

    public templateRenameFinish(id: number, instanceId: string) {
        this.dispatcher.handleViewAction<I.TEMPLATE_RENAME_FINISH>({
            actionType: Constants.TEMPLATE_RENAME_FINISH,

            id: id,
            instanceId: instanceId
        });
    }

    public templateMove(id: number, overwrite: boolean, currentDirId: number, dirId: number, instanceId: string) {
        this.dispatcher.handleViewAction<I.TEMPLATE_MOVE>({
            actionType: Constants.TEMPLATE_MOVE,
            id: id,
            overwrite: overwrite,
            currentDirId: currentDirId,
            dirId: dirId,
            instanceId: instanceId
        });
    }

    public templateMoveFinish(success: boolean, id: number, currentDirId: number, dirId: number, instanceId: string) {
        this.dispatcher.handleViewAction<I.TEMPLATE_MOVE_FINISH>({
            actionType: Constants.TEMPLATE_MOVE_FINISH,

            success: success,
            id: id,
            currentDirId: currentDirId,
            dirId: dirId,
            instanceId: instanceId
        });
    }

    public templateCreateSave(dirId: number, name: string, overwrite: boolean, data: any) {
        this.dispatcher.handleViewAction<I.TEMPLATE_CREATE_SAVE>({
            actionType: Constants.TEMPLATE_CREATE_SAVE,
            dirId: dirId,
            name: name,
            overwrite: overwrite,
            data: data
        });
    }

    public templateRemove(id: number, instanceId: string) {
        this.dispatcher.handleViewAction<I.TEMPLATE_DELETE>({
            actionType: Constants.TEMPLATE_DELETE,

            id: id,
            instanceId: instanceId
        });
    }

    public templateRemoveFinish(success: boolean, id: number, instanceId: string) {
        this.dispatcher.handleViewAction<I.TEMPLATE_DELETE_FINISH>({
            actionType: Constants.TEMPLATE_DELETE_FINISH,

            success: success,
            id: id,
            instanceId: instanceId
        });
    }

    public templateSetLock(id: number, type: string, fieldName: string, value: boolean) {
        this.dispatcher.handleViewAction<I.TEMPLATE_SET_LOCK>({
            actionType: Constants.TEMPLATE_SET_LOCK,

            id: id,
            type: type,
            fieldName: fieldName,
            value: value
        });
    }

    public workspaceViewShow(
        id: number,
        stateId?: number,
        distractFree: boolean = false,
        activeWidgets: IActiveWidgets = null
    ) {
        this.dispatcher.handleViewAction<I.WORKSPACE_VIEW_SHOW>({
            actionType: Constants.WORKSPACE_VIEW_SHOW,

            id: id,
            stateId: stateId,
            distractFree: distractFree,
            activeWidgets: activeWidgets
        });
    }

    public workspaceViewHide(id: number) {
        this.dispatcher.handleViewAction<I.WORKSPACE_VIEW_HIDE>({
            actionType: Constants.WORKSPACE_VIEW_HIDE,

            id: id
        });
    }

    public workspaceWidgetViewOpen(
        workspace: number,
        widget: number | string,
        stateId?: number,
        data?: any,
        query?: { [key: string]: string }
    ) {
        this.dispatcher.handleViewAction<I.WORKSPACE_WIDGET_VIEW_OPEN>({
            actionType: Constants.WORKSPACE_WIDGET_VIEW_OPEN,

            workspace: workspace,
            widget: widget,
            stateId: stateId,
            data: data,
            query: query
        });
    }

    public workspaceAppViewOpen(workspace: number) {
        this.dispatcher.handleViewAction<I.WORKSPACE_APP_VIEW_OPEN>({
            actionType: Constants.WORKSPACE_APP_VIEW_OPEN,

            workspace: workspace
        });
    }

    public workspaceAppViewClose(workspace: number) {
        this.dispatcher.handleViewAction<I.WORKSPACE_APP_VIEW_CLOSE>({
            actionType: Constants.WORKSPACE_APP_VIEW_CLOSE,

            workspace: workspace
        });
    }

    public workspaceViewClose(id: number, tabIndex: number, clearWorkspaceCache: boolean, doNotCloseTab?: boolean) {
        this.dispatcher.handleViewAction<I.WORKSPACE_VIEW_CLOSE>({
            actionType: Constants.WORKSPACE_VIEW_CLOSE,

            id: id,
            tabIndex: tabIndex,
            clearWorkspaceCache: clearWorkspaceCache,
            doNotCloseTab: doNotCloseTab
        });
    }

    public workspaceViewLoadContent(
        id: number,
        silent: boolean = false,
        stateId?: number,
        distractFree: boolean = false
    ) {
        this.dispatcher.handleViewAction<I.WORKSPACE_VIEW_CONTENT_LOAD>({
            actionType: Constants.WORKSPACE_VIEW_CONTENT_LOAD,

            id: id,
            silent: silent,
            stateId: stateId,
            distractFree: distractFree
        });
    }

    public workspaceViewLoadContentFinish(id: number, success: boolean = true) {
        this.dispatcher.handleViewAction<I.WORKSPACE_VIEW_CONTENT_LOAD_FINISH>({
            actionType: Constants.WORKSPACE_VIEW_CONTENT_LOAD_FINISH,

            id: id,
            success: success
        });
    }

    public workspaceAddWidget(id: number, theme: appTheme.IValuesCollection, tabID: number, pos?: number | any[]) {
        this.dispatcher.handleViewAction<I.WORKSPACE_ADD_WIDGET>({
            actionType: Constants.WORKSPACE_ADD_WIDGET,

            id: id,
            theme: theme,
            tabID: tabID,
            pos: pos
        });
    }

    public workspaceTemplateLoad(id: number) {
        this.dispatcher.handleViewAction<I.WORKSPACE_TEMPLATE_LOAD>({
            actionType: Constants.WORKSPACE_TEMPLATE_LOAD,

            id: id
        });
    }

    public workspaceTemplateLoadFinish(id: number) {
        this.dispatcher.handleViewAction<I.WORKSPACE_TEMPLATE_LOAD_FINISH>({
            actionType: Constants.WORKSPACE_TEMPLATE_LOAD_FINISH
        });
    }

    public workspaceTemplatesExplorerShow() {
        this.dispatcher.handleViewAction<I.WORKSPACE_TEMPLATES_EXPLORER_SHOW>({
            actionType: Constants.WORKSPACE_TEMPLATES_EXPLORER_SHOW
        });
    }

    public workspaceTemplatesExplorerHide() {
        this.dispatcher.handleViewAction<I.WORKSPACE_TEMPLATES_EXPLORER_HIDE>({
            actionType: Constants.WORKSPACE_TEMPLATES_EXPLORER_HIDE
        });
    }

    public showWorkspaceTemplateSidebar(workspace: number, show: boolean) {
        this.dispatcher.handleViewAction<I.SHOW_WORKSPACE_TEMPLATE_SIDEBAR>({
            actionType: Constants.SHOW_WORKSPACE_TEMPLATE_SIDEBAR,

            workspace: workspace,
            show: show
        });
    }

    public templatesExplorerShow() {
        this.dispatcher.handleViewAction<I.TEMPLATES_EXPLORER_SHOW>({
            actionType: Constants.TEMPLATES_EXPLORER_SHOW
        });
    }

    public templatesExplorerHide() {
        this.dispatcher.handleViewAction<I.TEMPLATES_EXPLORER_HIDE>({
            actionType: Constants.TEMPLATES_EXPLORER_HIDE
        });
    }

    public templatesExplorerClear(instanceId: string) {
        this.dispatcher.handleViewAction<I.TEMPLATES_EXPLORER_CLEAR>({
            actionType: Constants.TEMPLATES_EXPLORER_CLEAR,

            instanceId: instanceId
        });
    }

    public templatesExplorerSingleLoad(id: number, instanceId: string) {
        this.dispatcher.handleViewAction<I.TEMPLATES_EXPLORER_SINGLE_LOAD>({
            actionType: Constants.TEMPLATES_EXPLORER_SINGLE_LOAD,

            id: id,
            instanceId: instanceId
        });
    }

    public templatesExplorerSingleLoadFinish(id: number, instanceId: string) {
        this.dispatcher.handleViewAction<I.TEMPLATES_EXPLORER_SINGLE_LOAD_FINISH>({
            actionType: Constants.TEMPLATES_EXPLORER_SINGLE_LOAD_FINISH,

            id: id,
            instanceId: instanceId
        });
    }

    public templatesExplorerSetDisplayId(id: number, instanceId: string) {
        this.dispatcher.handleViewAction<I.TEMPLATES_EXPLORER_SET_DISPLAY_ID>({
            actionType: Constants.TEMPLATES_EXPLORER_SET_DISPLAY_ID,

            id: id,
            instanceId: instanceId
        });
    }

    public workspacesExplorerShow() {
        this.dispatcher.handleViewAction<I.WORKSPACES_EXPLORER_SHOW>({
            actionType: Constants.WORKSPACES_EXPLORER_SHOW
        });
    }

    public workspacesExplorerHide() {
        this.dispatcher.handleViewAction<I.WORKSPACES_EXPLORER_HIDE>({
            actionType: Constants.WORKSPACES_EXPLORER_HIDE
        });
    }

    public workspacesExplorerClear(instanceId: string) {
        this.dispatcher.handleViewAction<I.WORKSPACES_EXPLORER_CLEAR>({
            actionType: Constants.WORKSPACES_EXPLORER_CLEAR,
            instanceId: instanceId
        });
    }

    public workspacesExplorerSingleLoad(id: string | number, instanceID?: string) {
        this.dispatcher.handleViewAction<I.WORKSPACES_EXPLORER_SINGLE_LOAD>({
            actionType: Constants.WORKSPACES_EXPLORER_SINGLE_LOAD,
            id: id,
            instanceId: instanceID
        });
    }

    public workspacesExplorerSingleLoadFinish(id: string | number, instanceId: string) {
        this.dispatcher.handleViewAction<I.WORKSPACES_EXPLORER_SINGLE_LOAD_FINISH>({
            actionType: Constants.WORKSPACES_EXPLORER_SINGLE_LOAD_FINISH,
            id: id,
            instanceId: instanceId
        });
    }

    public workspacesExplorerSetDisplayId(id: string | number, instanceId: string) {
        this.dispatcher.handleViewAction<I.WORKSPACES_EXPLORER_SET_DISPLAY_ID>({
            actionType: Constants.WORKSPACES_EXPLORER_SET_DISPLAY_ID,
            id: id,
            instanceId: instanceId
        });
    }

    public setWorkspaceContentChanged(tabId: number, widget: number, contentChanged: boolean) {
        this.dispatcher.handleViewAction<I.SET_WORKSPACE_CONTENT_CHANGED>({
            actionType: Constants.WORKSPACE_SET_CONTENT_CHANGED,
            tabId: tabId,
            widget: widget,
            contentChanged: contentChanged
        });
    }

    public dropWorkspaceQuery(tabID: number) {
        this.dispatcher.handleViewAction<I.DROP_WORKSPACE_QUERY>({
            actionType: Constants.DROP_WORKSPACE_QUERY,
            tabID: tabID
        });
    }

    public workspacesNavSetSection(menuItem: MenusData.IMenuItem, mode: MenusData.MenuModes) {
        this.dispatcher.handleViewAction<I.WORKSPACES_NAV_SET_SECTION>({
            actionType: Constants.WORKSPACES_NAV_SET_SECTION,
            menuItem: menuItem,
            mode: mode
        });
    }

    public widgetEdit(
        workspace: number,
        data: IWidget,
        theme: appTheme.IValuesCollection,
        tabID: number,
        readOnly?: boolean
    ) {
        this.dispatcher.handleViewAction<I.WIDGET_EDIT>({
            actionType: Constants.WIDGET_EDIT,
            workspace: workspace,
            data: data,
            theme: theme,
            tabID: tabID,
            readOnly: readOnly
        });
    }

    public widgetEditShow() {
        this.dispatcher.handleViewAction<I.WIDGET_EDIT_SHOW>({
            actionType: Constants.WIDGET_EDIT_SHOW
        });
    }

    public widgetEditHide() {
        this.dispatcher.handleViewAction<I.WIDGET_EDIT_HIDE>({
            actionType: Constants.WIDGET_EDIT_HIDE
        });
    }

    public widgetSave(id: number, data: IWidget, ignoreWorkspace?: boolean, local?: boolean, workspaceID?: number) {
        this.dispatcher.handleViewAction<I.WIDGET_SAVE>({
            actionType: Constants.WIDGET_SAVE,
            id: id,
            data: data,
            ignoreWorkspace: ignoreWorkspace,
            local: local,
            workspaceID: workspaceID
        });
    }

    public widgetCreate(
        wsId: number,
        cid: number,
        data: IWidget,
        parentWidgetId?: number,
        wsName?: string,
        isVirtual?: boolean
    ) {
        this.dispatcher.handleViewAction<I.WIDGET_CREATE>({
            actionType: Constants.WIDGET_CREATE,
            wsId: wsId,
            parentWidgetId: parentWidgetId,
            cid: cid,
            data: data,
            wsName: wsName,
            isVirtual: isVirtual
        });
    }

    public widgetCreateFromWorkspace(cid, transformCallback) {
        this.dispatcher.handleViewAction<I.WIDGET_CREATE_FROM_WORKSPACE>({
            actionType: Constants.WIDGET_CREATE_FROM_WORKSPACE,
            cid: cid,
            transform: transformCallback
        });
    }

    public widgetRemove(id: number) {
        this.dispatcher.handleViewAction<I.WIDGET_REMOVE>({
            actionType: Constants.WIDGET_REMOVE,
            id: id
        });
    }

    public widgetDetach(id: number) {
        this.dispatcher.handleViewAction<I.WIDGET_DETACH>({
            actionType: Constants.WIDGET_DETACH,
            id: id
        });
    }

    public widgetAttach(
        wsId: number,
        id: number,
        x: number,
        y: number,
        remainSize: boolean = false,
        fresh: boolean = false,
        parentWidgetId?: number
    ) {
        this.dispatcher.handleViewAction<I.WIDGET_ATTACH>({
            actionType: Constants.WIDGET_ATTACH,
            wsId: wsId,
            parentWidgetId: parentWidgetId,
            id: id,
            x: x,
            y: y,
            remainSize: remainSize,
            fresh: fresh
        });
    }

    public widgetCreateLinkCallDialog(workspace: number, widget: number) {
        this.dispatcher.handleViewAction<I.WIDGET_CREATE_LINK_CALL_DIALOG>({
            actionType: Constants.WIDGET_CREATE_LINK_CALL_DIALOG,
            workspace: workspace,
            widget: widget
        });
    }

    public widgetCreateLink(
        workspace: number,
        widget: number,
        name: string,
        state: any,
        data: any,
        options: ICreateWidgetLinkOptions
    ) {
        this.dispatcher.handleViewAction<I.WIDGET_CREATE_LINK>({
            actionType: Constants.WIDGET_CREATE_LINK,
            workspace: workspace,
            widget: widget,
            name: name,
            state: state,
            widgetData: data,
            options: options
        });
    }

    public workspaceCreateLink(
        workspace: number,
        tab: number,
        name: string,
        params: string,
        options: ICreateLinkOptions
    ) {
        this.dispatcher.handleViewAction<I.WORKSPACE_CREATE_LINK>({
            actionType: Constants.WORKSPACE_CREATE_LINK,
            workspace: workspace,
            tab: tab,
            name: name,
            params: params,
            options: options
        });
    }

    public widgetSetState(widget: number, widgetState: IWidgetState) {
        this.dispatcher.handleViewAction<I.WIDGET_SET_STATE>({
            actionType: Constants.WIDGET_SET_STATE,
            widget: widget,
            widgetState: widgetState
        });
    }

    public widgetModuleLoadStart(type: string) {
        this.dispatcher.handleViewAction<I.WIDGET_MODULE_LOAD_START>({
            actionType: Constants.WIDGET_MODULE_LOAD_START,
            type: type
        });
    }

    public widgetModuleLoadFinish(type: string) {
        this.dispatcher.handleViewAction<I.WIDGET_MODULE_LOAD_FINISH>({
            actionType: Constants.WIDGET_MODULE_LOAD_FINISH,
            type: type
        });
    }

    public widgetSetLayout(workspace: number, widget: number, layout: IWidgetLayout) {
        this.dispatcher.handleViewAction<I.WIDGET_SET_LAYOUT>({
            actionType: Constants.WIDGET_SET_LAYOUT,
            workspace: workspace,
            widget: widget,
            layout: layout
        });
    }

    public widgetAddField(workspace: number, widget: number, x: number, y: number, field: string) {
        this.dispatcher.handleViewAction<I.WIDGET_ADD_FIELD>({
            actionType: Constants.WIDGET_ADD_FIELD,
            workspace: workspace,
            widget: widget,
            x: x,
            y: y,
            field: field
        });
    }

    public widgetRemoveField(workspace: number, widget: number, positionIndex: number, shift: number) {
        this.dispatcher.handleViewAction<I.WIDGET_REMOVE_FIELD>({
            actionType: Constants.WIDGET_REMOVE_FIELD,
            workspace: workspace,
            widget: widget,
            positionIndex: positionIndex,
            shift: shift
        });
    }

    public widgetSetHidden(tab: number, workspace: number, widgetID: number, hidden: boolean) {
        this.dispatcher.handleViewAction<I.WIDGET_SET_HIDDEN>({
            actionType: Constants.WIDGET_SET_HIDDEN,
            tab: tab,
            workspace: workspace,
            widget: widgetID,
            hidden: hidden
        });
    }

    public widgetDoClickToFilter(
        storeID: number,
        dataSourceID: number,
        attributes: DrillDownModule.IDDAttribute[],
        mode?: DrillDownModule.ClickToFilterMode,
        useAlways?: boolean
    ) {
        this.dispatcher.handleViewAction<I.WIDGET_DO_CLICK_TO_FILTER>({
            actionType: Constants.WIDGET_DO_CLICK_TO_FILTER,
            storeID: storeID,
            dataSourceID: dataSourceID,
            attributes: attributes,
            mode: mode,
            useAlways: useAlways
        });
    }

    public widgetDoDrillDown(
        tabID: number,
        drillDownWidgetID: number,
        level: number,
        dataSourceID: number,
        newAttributeFiltersCollection: IGlobalAttributeFilterCollection
    ) {
        this.dispatcher.handleViewAction<I.WIDGET_DO_DRILL_DOWN>({
            actionType: Constants.WIDGET_DO_DRILL_DOWN,
            tabID: tabID,
            drillDownWidgetID: drillDownWidgetID,
            level: level,
            dataSourceID: dataSourceID,
            newAttributeFiltersCollection: newAttributeFiltersCollection
        });
    }

    public widgetSetSubWidgetsHidden(
        tab: number,
        workspace: number,
        widgetID: number,
        hidden: boolean,
        subWidgets: IWidget[]
    ) {
        subWidgets.forEach((widget) => {
            if (widget.id !== widgetID) {
                this.widgetSetHidden(tab, workspace, widget.id, hidden);
            } else {
                this.showNotification_Fail(
                    `Wrong configuration: Widget with ${widgetID} ID has a sub widget with the same ID.`
                );
            }
        });
    }

    public widgetSetActiveSubWidgetsHidden(
        tab: number,
        workspace: number,
        widgetID: number,
        activeWidgetID: number,
        hidden: boolean,
        subWidgets: IWidget[]
    ) {
        subWidgets.forEach((widget) => {
            if (widget.id !== widgetID && widget.id === activeWidgetID) {
                this.widgetSetHidden(tab, workspace, widget.id, hidden);
            }
        });
    }

    public widgetUpdateOptions(workspace: number, widget: number, options: any) {
        this.dispatcher.handleViewAction<I.WIDGET_UPDATE_OPTIONS>({
            actionType: Constants.WIDGET_UPDATE_OPTIONS,
            workspace: workspace,
            widget: widget,
            options: options
        });
    }

    public widgetAddChild(
        wsid: number,
        id: number,
        theme: appTheme.IValuesCollection,
        tabID: number,
        pos?: number | any[]
    ) {
        this.dispatcher.handleViewAction<I.WIDGET_ADD_CHILD>({
            actionType: Constants.WIDGET_ADD_CHILD,
            wsid: wsid,
            id: id,
            pos: pos,
            theme: theme,
            tabID: tabID
        });
    }

    public widgetSetRole(tab: number, widget: number, role: string) {
        this.dispatcher.handleViewAction<I.WIDGET_SET_ROLE>({
            actionType: Constants.WIDGET_SET_ROLE,
            tab: tab,
            widget: widget,
            role: role
        });
    }

    public widgetUnsetRole(tab: number, widget: number) {
        this.dispatcher.handleViewAction<I.WIDGET_UNSET_ROLE>({
            actionType: Constants.WIDGET_UNSET_ROLE,
            tab: tab,
            widget: widget
        });
    }

    public widgetExportToExcel(widget: number, options: IExportWidgetToExcelOptions) {
        this.dispatcher.handleViewAction<I.WIDGET_EXPORT_TO_EXCEL>({
            actionType: Constants.WIDGET_EXPORT_TO_EXCEL,
            widget: widget,
            options: options
        });
    }

    public widgetExportToExcelFinish(widget: number) {
        this.dispatcher.handleViewAction<I.WIDGET_EXPORT_TO_EXCEL_FINISH>({
            actionType: Constants.WIDGET_EXPORT_TO_EXCEL_FINISH,
            widget: widget
        });
    }

    public widgetExportToPDF(
        workspace: number,
        widget: string,
        layout: string,
        papaersize: string,
        backendMethodConfig?: IBackendMethodConfiguratorValue
    ) {
        this.dispatcher.handleViewAction<I.WIDGET_EXPORT_TO_PDF>({
            actionType: Constants.WIDGET_EXPORT_TO_PDF,
            workspace: workspace,
            widget: widget,
            layout: layout,
            papersize: papaersize,
            backendMethodConfig: backendMethodConfig
        });
    }

    public widgetMultipleSetActive(workspace: number, parentWidgetID: number, activeWidgetID: number) {
        this.dispatcher.handleViewAction<I.WIDGET_MULTIPLE_SET_ACTIVE>({
            actionType: Constants.WIDGET_MULTIPLE_SET_ACTIVE,

            workspace: workspace,
            parentWidgetID: parentWidgetID,
            activeWidgetID: activeWidgetID
        });
    }

    public widgetDrillDownSetState(workspace: number, drillDownWidget: number, targetWidget: number) {
        this.dispatcher.handleViewAction<I.WIDGET_DRILLDOWN_SET_STATE>({
            actionType: Constants.WIDGET_DRILLDOWN_SET_STATE,

            workspace,
            drillDownWidget,
            targetWidget
        });
    }

    public widgetResetLayout(widget: number) {
        this.dispatcher.handleViewAction<I.WIDGET_RESET_LAYOUT>({
            actionType: Constants.WIDGET_RESET_LAYOUT,
            widget: widget
        });
    }

    public widgetRepaintOnDock() {
        this.dispatcher.handleViewAction<I.WIDGET_REPAINT_ON_DOCK>({
            actionType: Constants.WIDGET_REPAINT_ON_DOCK
        });
    }

    public permissionsInitialDataLoad() {
        this.dispatcher.handleViewAction<I.PERMISSIONS_INITIAL_DATA_LOAD>({
            actionType: Constants.PERMISSIONS_INITIAL_DATA_LOAD
        });
    }

    public permissionsInitialDataLoadFinish(success: boolean) {
        this.dispatcher.handleViewAction<I.PERMISSIONS_INITIAL_DATA_LOAD_FINISH>({
            actionType: Constants.PERMISSIONS_INITIAL_DATA_LOAD_FINISH,

            success: success
        });
    }

    public permissionsLoadDir(dirid: number, name: string) {
        this.dispatcher.handleViewAction<I.PERMISSIONS_LOAD_DIR>({
            actionType: Constants.PERMISSIONS_LOAD_DIR,

            dirid: dirid,
            name: name
        });
    }

    public permissionsLoadDirFinish(success: boolean) {
        this.dispatcher.handleViewAction<I.PERMISSIONS_LOAD_DIR_FINISH>({
            actionType: Constants.PERMISSIONS_LOAD_DIR_FINISH,

            success: success
        });
    }

    public permissionsDirShow(dirid: number, name: string) {
        this.dispatcher.handleViewAction<I.PERMISSIONS_DIR_SHOW>({
            actionType: Constants.PERMISSIONS_DIR_SHOW,

            dirid: dirid,
            name: name
        });
    }

    public permissionsDirHide() {
        this.dispatcher.handleViewAction<I.PERMISSIONS_DIR_HIDE>({
            actionType: Constants.PERMISSIONS_DIR_HIDE
        });
    }

    public permissionsDirSave(dirid: number, name: string, permissions: any) {
        this.dispatcher.handleViewAction<I.PERMISSIONS_DIR_SAVE>({
            actionType: Constants.PERMISSIONS_DIR_SAVE,

            dirid: dirid,
            name: name,
            permissions: permissions
        });
    }

    public addHomeDir(userid: number, name: string) {
        return this.dispatcher.handleViewAction<I.ADD_HOME_DIR>({
            actionType: Constants.ADD_HOME_DIR,
            userid: userid,
            name: name
        });
    }

    public deleteHomeDir(userid: number) {
        return this.dispatcher.handleViewAction<I.DELETE_HOME_DIR>({
            actionType: Constants.DELETE_HOME_DIR,
            userid: userid
        });
    }
    public getHomeDir(userid: number) {
        return this.dispatcher.handleViewAction<I.GET_HOME_DIR>({
            actionType: Constants.GET_HOME_DIR,
            userid: userid
        });
    }

    public componentShow(moduleName: string, componentProps: any) {
        this.dispatcher.handleViewAction<I.COMPONENT_SHOW>({
            actionType: Constants.COMPONENT_SHOW,
            moduleName,
            componentProps
        });
    }

    public configSetFieldValues(id: number, type: string, values: IDataEditValues) {
        this.dispatcher.handleViewAction<I.CONFIG_SET_FIELD_VALUES>({
            actionType: Constants.CONFIG_SET_FIELD_VALUES,
            id: id,
            type: type,
            values: values
        });
    }

    public configSetWidgetType(id: number, type: string) {
        this.dispatcher.handleViewAction<I.CONFIG_SET_WIDGET_TYPE>({
            actionType: Constants.CONFIG_SET_WIDGET_TYPE,
            id: id,
            type: type
        });
    }

    public configSetWidgetLayout(id: number, layout: any) {
        this.dispatcher.handleViewAction<I.CONFIG_SET_WIDGET_LAYOUT>({
            actionType: Constants.CONFIG_SET_WIDGET_LAYOUT,
            id: id,
            layout: layout
        });
    }

    public themeGet(id: number) {
        this.dispatcher.handleViewAction<I.THEME_GET>({
            actionType: Constants.THEME_GET,

            id: id
        });
    }

    public themeGetFinish(success: boolean, id: number) {
        this.dispatcher.handleViewAction<I.THEME_GET_FINISH>({
            actionType: Constants.THEME_GET_FINISH,

            success: success,
            id: id
        });
    }

    public themeGetList(force?: boolean) {
        this.dispatcher.handleViewAction<I.THEME_GET_LIST>({
            actionType: Constants.THEME_GET_LIST,

            force: force
        });
    }

    public themeGetListFinish(success: boolean) {
        this.dispatcher.handleViewAction<I.THEME_GET_LIST_FINISH>({
            actionType: Constants.THEME_GET_LIST_FINISH,

            success: success
        });
    }

    public themeCreate(def: JQueryDeferred<any>, name: string, data: any) {
        this.dispatcher.handleViewAction<I.THEME_CREATE>({
            actionType: Constants.THEME_CREATE,

            def: def,
            name: name,
            data: data
        });
    }

    public themeCreateFinish(success: boolean, name: string, data: any) {
        this.dispatcher.handleViewAction<I.THEME_CREATE_FINISH>({
            actionType: Constants.THEME_CREATE_FINISH,

            success: success,
            name: name,
            data: data
        });
    }

    public themeUpdate(def: JQueryDeferred<any>, id: number, name: string, data: any) {
        this.dispatcher.handleViewAction<I.THEME_UPDATE>({
            actionType: Constants.THEME_UPDATE,

            def: def,
            id: id,
            name: name,
            data: data
        });
    }

    public themeUpdateFinish(success: boolean, id: number, name: string, data: any) {
        this.dispatcher.handleViewAction<I.THEME_UPDATE_FINISH>({
            actionType: Constants.THEME_UPDATE_FINISH,

            success: success,
            id: id,
            name: name,
            data: data
        });
    }

    public themeRename(id: number, NewName: string) {
        this.dispatcher.handleViewAction<I.THEME_RENAME>({
            actionType: Constants.THEME_RENAME,

            id: id,
            NewName: NewName
        });
    }

    public themeRenameFinish(success: boolean, id: number, NewName: string) {
        this.dispatcher.handleViewAction<I.THEME_RENAME_FINISH>({
            actionType: Constants.THEME_RENAME_FINISH,

            success: success,
            id: id,
            NewName: NewName
        });
    }

    public themeDelete(id: number) {
        this.dispatcher.handleViewAction<I.THEME_DELETE>({
            actionType: Constants.THEME_DELETE,

            id: id
        });
    }

    public themeDeleteFinish(success: boolean, id: number) {
        this.dispatcher.handleViewAction<I.THEME_DELETE_FINISH>({
            actionType: Constants.THEME_DELETE_FINISH,

            success: success,
            id: id
        });
    }

    public appSettingsGetDescription() {
        this.dispatcher.handleViewAction<I.APP_SETTINGS_GET_DESCRIPTION>({
            actionType: Constants.APP_SETTINGS_GET_DESCRIPTION
        });
    }

    public appSettingsGetDescriptionFinish() {
        this.dispatcher.handleViewAction<I.APP_SETTINGS_GET_DESCRIPTION_FINISH>({
            actionType: Constants.APP_SETTINGS_GET_DESCRIPTION_FINISH
        });
    }

    public appSettingsGet(names?: string[], settings?: any, forEdit?: boolean) {
        this.dispatcher.handleViewAction<I.APP_SETTINGS_GET>({
            actionType: Constants.APP_SETTINGS_GET,

            names,
            settings,
            forEdit
        });
    }

    public appSettingsGetFinish(data?) {
        this.dispatcher.handleViewAction<I.APP_SETTINGS_GET_FINISH>({
            actionType: Constants.APP_SETTINGS_GET_FINISH,
            data
        });
    }

    public appSettingsGetDefaultTheme() {
        this.dispatcher.handleViewAction<I.APP_SETTINGS_GET_DEFAULT_THEME>({
            actionType: Constants.APP_SETTINGS_GET_DEFAULT_THEME
        });
    }

    public appSettingsGetDefaultThemeFinish(data?) {
        this.dispatcher.handleViewAction<I.APP_SETTINGS_GET_DEFAULT_THEME_FINISH>({
            actionType: Constants.APP_SETTINGS_GET_DEFAULT_THEME_FINISH,
            data
        });
    }

    public appSettingsSet(values: any) {
        this.dispatcher.handleViewAction<I.APP_SETTINGS_SET>({
            actionType: Constants.APP_SETTINGS_SET,
            values: values
        });
    }
    public appSettingsTestEmailSend(values: any) {
        this.dispatcher.handleViewAction<I.APP_SETTINGS_TEST_EMAIL_SEND>({
            actionType: Constants.APP_SETTINGS_TEST_EMAIL_SEND,
            values: values
        });
    }

    public appSettingsSetFinish(data?) {
        this.dispatcher.handleViewAction<I.APP_SETTINGS_SET_FINISH>({
            actionType: Constants.APP_SETTINGS_SET_FINISH,
            data
        });
    }

    public appSettingsApplyChanges() {
        this.dispatcher.handleViewAction<I.APP_SETTINGS_SET_FINISH>({
            actionType: Constants.APP_SETTINGS_APPLY_CHANGES
        });
    }

    public setWidgetTabTheme(tab: number, theme: any) {
        this.dispatcher.handleViewAction<I.SET_WIDGET_TAB_THEME>({
            actionType: Constants.SET_WIDGET_TAB_THEME,
            tab: tab,
            theme: theme
        });
    }

    public userPermissionsLoadDescription() {
        this.dispatcher.handleViewAction<I.USER_PERMISSIONS_LOAD_DESCRIPTION>({
            actionType: Constants.USER_PERMISSIONS_LOAD_DESCRIPTION
        });
    }

    public userPermissionsLoadUser(user?: number) {
        this.dispatcher.handleViewAction<I.USER_PERMISSIONS_LOAD_USER>({
            actionType: Constants.USER_PERMISSIONS_LOAD_USER,
            user: user
        });
    }

    public usersGroupsDataUpdated() {
        this.dispatcher.handleViewAction<I.USERS_GROUPS_DATA_UPDATED>({
            actionType: Constants.USERS_GROUPS_DATA_UPDATED
        });
    }

    public pageVisibilityChange(isVisibile: boolean) {
        this.dispatcher.handleViewAction<I.PAGE_VISIBILITY_CHANGE>({
            actionType: Constants.PAGE_VISIBILITY_CHANGE,
            isVisibile: isVisibile
        });
    }

    public filtersAreOnShelf(workspace: number, tab: number, namespace: string) {
        this.dispatcher.handleViewAction<I.FILTERS_ARE_ON_SHELF>({
            actionType: Constants.FILTERS_ARE_ON_SHELF,
            workspace: workspace,
            tab,
            namespace
        });
    }

    public resetFilters(workspace: number, tab: number, namespace: string, widgetID?: number) {
        this.dispatcher.handleViewAction<I.FILTERS_RESET>({
            actionType: Constants.FILTERS_RESET,
            workspace: workspace,
            tab,
            namespace,
            widgetID
        });
    }

    public workspaceApplyChanges(tab: number, namespace: string, changesType: I.ChangesType) {
        this.dispatcher.handleViewAction<I.WORKSPACE_APPLY_CHANGES>({
            actionType: Constants.WORKSPACE_APPLY_CHANGES,
            tab,
            namespace,
            changesType: changesType
        });
    }

    public toolbarBottons_CancelOn() {
        this.dispatcher.handleViewAction<I.TOOLBAR_BUTTONS_CANCEL_ON>({
            actionType: Constants.TOOLBAR_BUTTONS_CANCEL_ON
        });
    }

    public toolbarBottons_CancelOff() {
        this.dispatcher.handleViewAction<I.TOOLBAR_BUTTONS_CANCEL_ON>({
            actionType: Constants.TOOLBAR_BUTTONS_CANCEL_OFF
        });
    }

    public toolbarBottons_SwitchApply(tabID: number, value: boolean) {
        this.dispatcher.handleViewAction<I.TOOLBAR_BUTTONS_SWITCH_APPLY>({
            actionType: Constants.TOOLBAR_BUTTONS_SWITCH_APPLY,
            tabID: tabID,
            value: value
        });
    }

    public toggleFullScreenMode() {
        this.dispatcher.handleViewAction<I.TOGGLE_FULL_SCREEN_MODE>({
            actionType: Constants.TOGGLE_FULL_SCREEN_MODE
        });
    }

    public setLockEscape(value: boolean) {
        this.dispatcher.handleViewAction<I.SET_LOCK_ESCAPE>({
            actionType: Constants.SET_LOCK_ESCAPE,
            value: value
        });
    }

    public callDataEditDialog(
        tabID: number,
        options: I.ICallDataEditDialogOptions,
        sourceWidgetID?: number,
        customDialogID?: string
    ) {
        this.dispatcher.handleViewAction<I.CALL_DATA_EDIT_DIALOG>({
            actionType: Constants.CALL_DATA_EDIT_DIALOG,
            tabID: tabID,
            options: options,
            sourceWidgetID: sourceWidgetID,
            customDialogID: customDialogID
        });
    }

    public loadData<TParams>(storeID: number, parameters: TParams) {
        this.dispatcher.handleViewAction<I.LOAD_DATA<TParams>>({
            actionType: Constants.LOAD_DATA,
            storeID: storeID,
            parameters: parameters
        });
    }

    public loadDataFinish(storeID: number) {
        this.dispatcher.handleViewAction<I.LOAD_DATA_FINISH>({
            actionType: Constants.LOAD_DATA_FINISH,
            storeID: storeID
        });
    }

    public abstractAction<T extends I.IAbstractActionPayload>(
        workspace: number,
        tab: number,
        widget: number,
        payload: T
    ) {
        this.dispatcher.handleViewAction<I.ABSTRACT_ACTION>({
            actionType: Constants.ABSTRACT_ACTION,

            workspace,
            tab,
            widget,
            payload
        });
    }
}
